import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Calendar,
  User,
  Briefcase,
  TrendingUp,
  Filter,
  Download,
  History,
  ChevronDown,
  Car,
  Coffee,
  Shield,
  MapPin,
  AlertTriangle,
} from 'lucide-react';
import { cn, localToday, localDaysAgo } from '@/lib/utils';
import { toast } from 'sonner';
import { TimeAuditHistoryDialog } from '@/components/TimeAuditHistoryDialog';

const STATUS_CONFIG = {
  pending: { label: 'На проверке', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
  approved: { label: 'Подтверждено', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
  rejected: { label: 'Отклонено', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  disputed: { label: 'Оспорено', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: AlertCircle },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(isoStr) {
  if (!isoStr) return '-';
  const date = new Date(isoStr);
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatMinutes(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h > 0 && m > 0) return `${h}ч ${m}мин`;
  if (h > 0) return `${h}ч`;
  return `${m}мин`;
}

const GPS_STATUS_CONFIG = {
  verified: { label: 'GPS ✓', color: 'text-green-600 bg-green-50', icon: Shield },
  warning: { label: 'GPS ~300м', color: 'text-amber-600 bg-amber-50', icon: AlertTriangle },
  flagged: { label: 'GPS далеко', color: 'text-red-600 bg-red-50', icon: AlertTriangle },
  unavailable: { label: 'GPS нет', color: 'text-gray-400 bg-gray-50', icon: MapPin },
};

// Fair Hours Detail Row — expandable within the table
function FairHoursDetail({ submission, api, onRefresh }) {
  const [disputeAction, setDisputeAction] = useState(null); // { disputeId, action }
  const [actionLoading, setActionLoading] = useState(false);

  const blocks = submission.fair_hours_breakdown || [];
  const disputes = submission.disputes || [];
  const mode = submission.calculation_mode;

  const handleDisputeAction = async (disputeId, action) => {
    setActionLoading(true);
    try {
      // Use the existing resolve endpoint or a block-dispute endpoint
      await api.post(`/time/submissions/${submission.id}/resolve-block-dispute`, {
        dispute_id: disputeId,
        action: action, // 'approve' | 'reject'
      });
      toast.success(action === 'approve' ? 'Спор принят — часы обновлены' : 'Спор отклонён');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="px-4 pb-4 space-y-3">
      {/* Mode badge + totals */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="text-xs">
          Режим: {mode === 'smart' ? 'Smart' : mode === 'per_job' ? 'По заказам' : 'Весь день'}
        </Badge>
        {submission.total_paid_minutes != null && (
          <span className="text-xs text-muted-foreground">
            Оплачено: <strong className="text-green-600">{formatMinutes(submission.total_paid_minutes)}</strong>
            {' '}| Работа: {formatMinutes(submission.total_work_minutes)}
            {' '}| Дорога: {formatMinutes(submission.total_travel_minutes)}
            {' '}| Перерыв: {formatMinutes(submission.total_break_minutes)}
          </span>
        )}
      </div>

      {/* Blocks timeline */}
      {blocks.length > 0 ? (
        <div className="space-y-1">
          {blocks.map((block, idx) => {
            const startT = block.start_time ? new Date(block.start_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
            const endT = block.end_time ? new Date(block.end_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
            const gpsConf = GPS_STATUS_CONFIG[block.gps_start_status];
            const GpsIcon = gpsConf?.icon;

            // Check if there's a dispute for this block
            const dispute = disputes.find(d => d.block_index === idx);

            return (
              <div key={idx} className={cn(
                "flex items-center gap-2 p-2 rounded-lg text-sm border",
                block.type === 'work' && "bg-green-50/50 dark:bg-green-900/5 border-green-200/50",
                block.type === 'travel' && "bg-blue-50/50 dark:bg-blue-900/5 border-blue-200/50",
                block.type === 'break' && "bg-gray-50/50 dark:bg-gray-900/5 border-gray-200/50",
              )}>
                {/* Icon */}
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0",
                  block.type === 'work' && "bg-green-100",
                  block.type === 'travel' && "bg-blue-100",
                  block.type === 'break' && "bg-gray-100",
                )}>
                  {block.type === 'work' && <Briefcase className="h-3.5 w-3.5 text-green-600" />}
                  {block.type === 'travel' && <Car className="h-3.5 w-3.5 text-blue-600" />}
                  {block.type === 'break' && <Coffee className="h-3.5 w-3.5 text-gray-500" />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-xs truncate">
                      {block.type === 'work' ? (block.order_title || 'Работа') :
                       block.type === 'travel' ? 'Дорога' : 'Перерыв'}
                    </span>
                    {block.client_name && (
                      <span className="text-[10px] text-muted-foreground truncate">({block.client_name})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="font-mono">{startT} — {endT}</span>
                    <span className="font-semibold">{formatMinutes(block.duration_minutes)}</span>
                    {block.osrm_distance_km && <span>{block.osrm_distance_km}км</span>}
                  </div>
                </div>

                {/* GPS badge */}
                {gpsConf && GpsIcon && (
                  <Badge variant="outline" className={cn("text-[10px] gap-0.5", gpsConf.color)}>
                    <GpsIcon className="h-3 w-3" />
                    {block.gps_start_distance != null && `${Math.round(block.gps_start_distance)}м`}
                  </Badge>
                )}

                {/* Paid status */}
                <span className={cn("text-[10px] font-medium shrink-0",
                  block.is_paid ? "text-green-600" : "text-gray-400"
                )}>
                  {block.is_paid ? 'оплач.' : 'не оплач.'}
                </span>

                {/* Dispute indicator */}
                {dispute && (
                  <div className="flex items-center gap-1 shrink-0">
                    {dispute.status === 'pending' ? (
                      <div className="flex gap-1">
                        <button
                          className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200"
                          onClick={() => handleDisputeAction(dispute.id, 'approve')}
                          disabled={actionLoading}
                        >
                          ✓ {dispute.worker_minutes}мин
                        </button>
                        <button
                          className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200"
                          onClick={() => handleDisputeAction(dispute.id, 'reject')}
                          disabled={actionLoading}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <Badge variant="outline" className={cn("text-[10px]",
                        dispute.status === 'approved' && 'border-green-300 text-green-600',
                        dispute.status === 'rejected' && 'border-red-300 text-red-600',
                      )}>
                        {dispute.status === 'approved' ? `✓${dispute.worker_minutes}м` : '✕ откл.'}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Детализация по блокам недоступна (режим: {mode || 'full_day'})</p>
      )}

      {/* Speed diff info */}
      {blocks.some(b => b.speed_diff_minutes != null && b.speed_diff_minutes > 0) && (
        <div className="text-xs text-green-600 flex items-center gap-1">
          <TrendingUp className="h-3 w-3" />
          Работник завершил некоторые заказы раньше срока
        </div>
      )}
    </div>
  );
}

// Manager/Admin Time Approval Page
function TimeApprovalPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => localDaysAgo(7));
  const [dateTo, setDateTo] = useState(() => localToday());
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Resolve dispute dialog
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveSubmission, setResolveSubmission] = useState(null);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [reviewingAdj, setReviewingAdj] = useState(null); // adjustment being reviewed
  const [adjComment, setAdjComment] = useState('');
  const [adjApprovedH, setAdjApprovedH] = useState(0);
  const [adjApprovedM, setAdjApprovedM] = useState(0);
  const [disputedSubs, setDisputedSubs] = useState([]);
  const [blockDisputeLoading, setBlockDisputeLoading] = useState(false);

  // Fetch workers for audit history filter
  useEffect(() => {
    api.get('/workers').then(res => setWorkers(res.data)).catch(() => {});
  }, [api]);

  // Fetch ALL time adjustment requests (pending shown at top, resolved in history)
  const fetchAdjustments = useCallback(async () => {
    try {
      const res = await api.get('/time-adjustments');
      setAdjustments(res.data || []);
    } catch (err) {
      console.error('Failed to load adjustments:', err);
    }
  }, [api]);

  useEffect(() => { fetchAdjustments(); }, [fetchAdjustments]);

  // Fetch disputed submissions (block disputes pending admin review)
  const fetchDisputedSubs = useCallback(async () => {
    try {
      const res = await api.get('/time/submissions?status=disputed');
      setDisputedSubs(res.data || []);
    } catch (err) {
      console.error('Failed to load disputed subs:', err);
    }
  }, [api]);

  useEffect(() => { fetchDisputedSubs(); }, [fetchDisputedSubs]);

  const handleBlockDisputeAction = async (subId, disputeId, action) => {
    setBlockDisputeLoading(true);
    try {
      await api.post(`/time/submissions/${subId}/resolve-block-dispute`, {
        dispute_id: disputeId,
        action,
      });
      toast.success(action === 'approve' ? 'Спор принят — часы обновлены' : 'Спор отклонён');
      fetchDisputedSubs();
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setBlockDisputeLoading(false);
    }
  };

  const handleReviewAdjustment = async (id, status) => {
    setAdjustmentLoading(true);
    try {
      await api.put(`/time-adjustments/${id}/review`, {
        status,
        approved_hours: status === 'approved' ? adjApprovedH : null,
        approved_minutes: status === 'approved' ? adjApprovedM : null,
        manager_comment: adjComment.trim() || null,
      });
      toast.success(status === 'approved' ? 'Доп. время одобрено' : 'Запрос отклонён');
      setReviewingAdj(null);
      setAdjComment('');
      fetchAdjustments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const res = await api.get(`/time/submissions?${params.toString()}`);
      setSubmissions(res.data);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      toast.error('Failed to load time submissions');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/time/submissions/${id}/approve`);
      toast.success(t('time.approvedMsg'));
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get(`/time/export/csv?date_from=${dateFrom}&date_to=${dateTo}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `time_report_${dateFrom}_${dateTo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleRejectClick = (id) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/time/submissions/${rejectingId}/reject`, { reason: rejectReason });
      toast.success(t('time.rejectedMsg'));
      setRejectDialogOpen(false);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveDispute = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/time/submissions/${resolveSubmission.id}/resolve`, { action });
      toast.success(action === 'approve' ? 'Часы работника приняты' : 'Оставлены часы системы');
      setResolveDialogOpen(false);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  // Stats
  const pendingCount = submissions.filter(s => s.status === 'pending' || s.status === 'disputed').length;
  const totalHours = submissions.reduce((sum, s) => sum + (s.total_hours || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('time.pageTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('time.pageSubtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAuditDialogOpen(true)} data-testid="audit-history-btn">
            <History className="h-4 w-4 mr-2" />
            {t('time.auditHistory')}
          </Button>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            {t('time.exportCSV')}
          </Button>
        </div>
      </div>

      {/* Pending Time Adjustment Requests */}
      {(() => {
        const pendingAdj = adjustments.filter(a => a.status === 'pending');
        const resolvedAdj = adjustments.filter(a => a.status !== 'pending');
        return (
          <>
            {pendingAdj.length > 0 && (
              <Card className="cgc-card border-amber-300 dark:border-amber-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <Clock className="h-4 w-4" />
                    Запросы на доп. время ({pendingAdj.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingAdj.map(adj => (
                    <div key={adj.id} className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold">
                            {adj.worker_name}
                            {adj.is_appeal && <Badge variant="outline" className="ml-2 text-[10px] border-amber-400 text-amber-700">Апелляция</Badge>}
                          </p>
                          <p className="text-xs text-muted-foreground">{adj.date} · +{adj.requested_hours}ч {adj.requested_minutes > 0 ? `${adj.requested_minutes}мин` : ''}</p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">«{adj.reason}»</p>
                        </div>
                        {reviewingAdj?.id !== adj.id && (
                          <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline"
                              className="h-8 text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => { setReviewingAdj(adj); setAdjApprovedH(adj.requested_hours); setAdjApprovedM(adj.requested_minutes); setAdjComment(''); }}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Одобрить
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => { setReviewingAdj(adj); setAdjApprovedH(0); setAdjApprovedM(0); setAdjComment(''); }}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" /> Отклонить
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Inline review form */}
                      {reviewingAdj?.id === adj.id && (
                        <div className="pt-2 border-t border-amber-200 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Одобрить часов</Label>
                              <select value={adjApprovedH} onChange={e => setAdjApprovedH(parseInt(e.target.value))}
                                className="w-full h-9 mt-1 rounded-md border bg-background px-2 text-sm">
                                {[0,1,2,3,4,5,6,7,8].map(h => <option key={h} value={h}>{h} ч</option>)}
                              </select>
                            </div>
                            <div>
                              <Label className="text-xs">Минут</Label>
                              <select value={adjApprovedM} onChange={e => setAdjApprovedM(parseInt(e.target.value))}
                                className="w-full h-9 mt-1 rounded-md border bg-background px-2 text-sm">
                                {[0,15,30,45].map(m => <option key={m} value={m}>{m} мин</option>)}
                              </select>
                            </div>
                          </div>
                          <Textarea
                            value={adjComment}
                            onChange={e => setAdjComment(e.target.value)}
                            placeholder={adj.is_appeal ? "Обязательно укажите причину решения..." : "Комментарий для работника (необязательно)..."}
                            rows={2}
                            className="text-sm"
                          />
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => setReviewingAdj(null)}>
                              Отмена
                            </Button>
                            <Button size="sm" variant="outline"
                              className="flex-1 text-red-600 border-red-200"
                              disabled={adjustmentLoading}
                              onClick={() => handleReviewAdjustment(adj.id, 'rejected')}
                            >
                              {adjustmentLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                              Отклонить
                            </Button>
                            <Button size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              disabled={adjustmentLoading || (adjApprovedH === 0 && adjApprovedM === 0)}
                              onClick={() => handleReviewAdjustment(adj.id, 'approved')}
                            >
                              {adjustmentLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                              Одобрить
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Resolved Adjustments History */}
            {resolvedAdj.length > 0 && (
              <Card className="cgc-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                    <History className="h-4 w-4" />
                    История запросов на доп. время ({resolvedAdj.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {resolvedAdj.slice(0, 20).map(adj => (
                    <div key={adj.id} className={cn(
                      "p-2.5 rounded-lg border text-sm",
                      adj.status === 'approved' && "bg-green-50/50 dark:bg-green-900/5 border-green-200/50",
                      adj.status === 'rejected' && "bg-red-50/50 dark:bg-red-900/5 border-red-200/50",
                    )}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-xs">{adj.worker_name}</span>
                          {adj.is_appeal && <Badge variant="outline" className="text-[9px] h-4 border-amber-400 text-amber-600">Апелляция</Badge>}
                        </div>
                        <Badge variant="outline" className={cn("text-[10px]",
                          adj.status === 'approved' && "border-green-400 text-green-700",
                          adj.status === 'rejected' && "border-red-400 text-red-700",
                        )}>
                          {adj.status === 'approved' ? `+${adj.approved_hours}ч${adj.approved_minutes > 0 ? ` ${adj.approved_minutes}м` : ''}` : 'Отклонено'}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {adj.date} · Запрос: +{adj.requested_hours}ч {adj.requested_minutes > 0 ? `${adj.requested_minutes}мин` : ''}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">Причина: {adj.reason}</p>
                      {adj.manager_comment && (
                        <p className="text-[11px] mt-0.5 italic">Ответ: «{adj.manager_comment}»</p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}

      {/* Pending Block Disputes */}
      {disputedSubs.length > 0 && (
        <Card className="cgc-card border-purple-300 dark:border-purple-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-purple-700 dark:text-purple-400">
              <AlertCircle className="h-4 w-4" />
              Оспорено работниками ({disputedSubs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {disputedSubs.map(sub => {
              const pendingDisputes = (sub.disputes || []).filter(d => d.status === 'pending');
              if (pendingDisputes.length === 0) return null;
              return (
                <div key={sub.id} className="p-3 rounded-lg border border-purple-200 bg-purple-50 dark:bg-purple-900/10 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{sub.worker_name}</p>
                      <p className="text-xs text-muted-foreground">{sub.date} · {sub.total_paid_minutes != null ? formatMinutes(sub.total_paid_minutes) : `${sub.total_hours}ч`}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700">
                      {pendingDisputes.length} блок(ов)
                    </Badge>
                  </div>
                  {pendingDisputes.map(dispute => (
                    <div key={dispute.id} className="p-2 rounded border border-purple-100 bg-white dark:bg-purple-950/20 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">
                          {dispute.block_type === 'work' ? '💼 Работа' : dispute.block_type === 'travel' ? '🚗 Дорога' : `Блок`} #{dispute.block_index + 1}
                        </span>
                        <span className="text-muted-foreground">
                          Было: <strong>{formatMinutes(dispute.system_minutes)}</strong> → Просит: <strong className="text-purple-700">{formatMinutes(dispute.worker_minutes)}</strong>
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground italic">«{dispute.reason}»</p>
                      <div className="flex gap-2">
                        <button
                          className="flex-1 text-[11px] px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                          disabled={blockDisputeLoading}
                          onClick={() => handleBlockDisputeAction(sub.id, dispute.id, 'approve')}
                        >
                          ✓ Принять {formatMinutes(dispute.worker_minutes)}
                        </button>
                        <button
                          className="flex-1 text-[11px] px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                          disabled={blockDisputeLoading}
                          onClick={() => handleBlockDisputeAction(sub.id, dispute.id, 'reject')}
                        >
                          ✕ Оставить {formatMinutes(dispute.system_minutes)}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cgc-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">{t('time.pending')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">{totalHours.toFixed(1)}{t('time.hours')}</p>
                <p className="text-xs text-muted-foreground">{t('time.hoursLabel')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="cgc-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="time-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('time.all')}</SelectItem>
                  <SelectItem value="pending">На проверке</SelectItem>
                  <SelectItem value="disputed">Оспорено</SelectItem>
                  <SelectItem value="approved">Подтверждено</SelectItem>
                  <SelectItem value="rejected">Отклонено</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">{t('time.periodStart')}:</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
                data-testid="time-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">{t('time.periodEnd')}:</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
                data-testid="time-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card className="cgc-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{t('time.noSubmissions')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('time.date')}</TableHead>
                  <TableHead>{t('time.worker')}</TableHead>
                  <TableHead>{t('time.dayStart')}</TableHead>
                  <TableHead>{t('time.dayEnd')}</TableHead>
                  <TableHead className="text-center">{t('time.totalHours')}</TableHead>
                  <TableHead className="text-center">{t('time.ordersCompleted')}</TableHead>
                  <TableHead>{t('time.status')}</TableHead>
                  <TableHead className="text-right">{t('time.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((sub) => {
                  const statusCfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusCfg.icon;
                  const isExpanded = expandedId === sub.id;
                  const hasFairHours = sub.fair_hours_breakdown && sub.fair_hours_breakdown.length > 0;
                  const hasDisputes = sub.disputes && sub.disputes.length > 0;
                  return (
                    <React.Fragment key={sub.id}>
                      <TableRow
                        data-testid={`time-submission-${sub.id}`}
                        className={cn("cursor-pointer", isExpanded && "bg-muted/30")}
                        onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1">
                            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform text-muted-foreground", isExpanded && "rotate-180")} />
                            {formatDate(sub.date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {sub.worker_name || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell>{formatTime(sub.day_start)}</TableCell>
                        <TableCell>{formatTime(sub.day_end)}</TableCell>
                        <TableCell className="text-center font-mono">
                          <div>
                            {sub.total_hours?.toFixed(1) || 0}{t('time.hours')}
                            {hasFairHours && sub.calculation_mode !== 'full_day' && (
                              <div className="text-[10px] text-muted-foreground">
                                {sub.calculation_mode === 'smart' ? 'smart' : 'per_job'}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Badge variant="outline">{sub.orders_completed || 0}</Badge>
                            {hasDisputes && (
                              <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-600">
                                {sub.disputes.filter(d => d.status === 'pending').length} спор
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("text-xs", statusCfg.color)}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {t(`time.${sub.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {sub.status === 'pending' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleApprove(sub.id)}
                                disabled={actionLoading}
                                data-testid={`approve-${sub.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                {t('time.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleRejectClick(sub.id)}
                                disabled={actionLoading}
                                data-testid={`reject-${sub.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                {t('time.reject')}
                              </Button>
                            </div>
                          )}
                          {sub.status === 'disputed' && (
                            <div className="flex flex-col items-end gap-1">
                              <div className="text-xs text-purple-600 mb-1">
                                Предлагает: <span className="font-bold">{sub.disputed_hours?.toFixed(1)}ч</span>
                                {' '}(было {sub.total_hours?.toFixed(1)}ч)
                              </div>
                              {sub.dispute_reason && (
                                <div className="text-xs text-muted-foreground max-w-[200px] truncate" title={sub.dispute_reason}>
                                  «{sub.dispute_reason}»
                                </div>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-purple-600 border-purple-200 hover:bg-purple-50"
                                onClick={() => { setResolveSubmission(sub); setResolveDialogOpen(true); }}
                                disabled={actionLoading}
                              >
                                Рассмотреть спор
                              </Button>
                            </div>
                          )}
                          {sub.status === 'approved' && sub.reviewer_name && (
                            <span className="text-xs text-muted-foreground">
                              {sub.auto_approved ? 'Авто (работник согласился)' : `${t('time.reviewedBy')}: ${sub.reviewer_name}`}
                            </span>
                          )}
                          {sub.status === 'rejected' && sub.rejection_reason && (
                            <span className="text-xs text-red-600" title={sub.rejection_reason}>
                              {sub.rejection_reason.slice(0, 30)}...
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Expanded fair hours detail */}
                      {isExpanded && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={8} className="p-0">
                            <FairHoursDetail submission={sub} api={api} onRefresh={() => { fetchSubmissions(); fetchDisputedSubs(); }} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('time.reject')}</DialogTitle>
            <DialogDescription>{t('time.rejectReason')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('time.rejectReasonPlaceholder')}
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={actionLoading || !rejectReason.trim()}
              data-testid="reject-confirm-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('time.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dispute Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Рассмотрение спора</DialogTitle>
            <DialogDescription>
              Работник <strong>{resolveSubmission?.worker_name}</strong> за {resolveSubmission?.date}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 text-sm">
            <div className="p-3 rounded-lg bg-muted/50 grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Часы системы</p>
                <p className="text-xl font-bold">{resolveSubmission?.total_hours?.toFixed(1)}ч</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Часы работника</p>
                <p className="text-xl font-bold text-purple-600">{resolveSubmission?.disputed_hours?.toFixed(1)}ч</p>
              </div>
            </div>
            {resolveSubmission?.dispute_reason && (
              <div className="p-3 rounded-lg border border-purple-200 bg-purple-50 dark:bg-purple-900/10">
                <p className="text-xs text-muted-foreground mb-1">Причина от работника:</p>
                <p>«{resolveSubmission.dispute_reason}»</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="outline"
              className="text-red-600 border-red-200"
              onClick={() => handleResolveDispute('reject')}
              disabled={actionLoading}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Оставить системные ({resolveSubmission?.total_hours?.toFixed(1)}ч)
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => handleResolveDispute('approve')}
              disabled={actionLoading}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Принять ({resolveSubmission?.disputed_hours?.toFixed(1)}ч)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit History Dialog */}
      <TimeAuditHistoryDialog
        open={auditDialogOpen}
        onOpenChange={setAuditDialogOpen}
        workers={workers}
      />
    </div>
  );
}

// Worker's own time view
function WorkerTimePage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeSub, setDisputeSub] = useState(null);
  const [disputeHours, setDisputeHours] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const periodStart = localToday(new Date(now.getFullYear(), now.getMonth(), 1));
      const periodEnd = localToday();
      
      const [subsRes, summaryRes] = await Promise.all([
        api.get('/time/worker/me/submissions'),
        api.get(`/time/worker/me/summary?period_start=${periodStart}&period_end=${periodEnd}`)
      ]);
      setSubmissions(subsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch time data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAccept = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/time/submissions/${id}/accept`);
      toast.success('Принято автоматически');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisputeSubmit = async () => {
    if (!disputeHours || !disputeReason.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/time/submissions/${disputeSub.id}/dispute`, {
        disputed_hours: parseFloat(disputeHours),
        dispute_reason: disputeReason,
      });
      toast.success('Запрос отправлен менеджеру');
      setDisputeDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('time.myTime')}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t('time.myTimeSubtitle')}</p>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="cgc-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              {t('time.summary')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
                <p className="text-2xl font-bold text-green-600 tabular-nums">
                  {summary.approved_hours?.toFixed(1) || 0}{t('time.hours')}
                </p>
                <p className="text-xs text-muted-foreground">{t('time.approvedHours')}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                <p className="text-2xl font-bold text-amber-600 tabular-nums">
                  {summary.pending_hours?.toFixed(1) || 0}{t('time.hours')}
                </p>
                <p className="text-xs text-muted-foreground">{t('time.pendingHours')}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold tabular-nums">
                  {summary.total_hours?.toFixed(1) || 0}{t('time.hours')}
                </p>
                <p className="text-xs text-muted-foreground">{t('time.hoursLabel')}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold tabular-nums">{summary.orders_completed || 0}</p>
                <p className="text-xs text-muted-foreground">{t('time.ordersCompleted')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Submissions */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {t('time.title')}
        </h2>
        {submissions.length === 0 ? (
          <Card className="cgc-card">
            <CardContent className="p-8 text-center">
              <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">{t('time.noSubmissions')}</p>
            </CardContent>
          </Card>
        ) : (
          submissions.map((sub) => {
            const statusCfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusCfg.icon;
            return (
              <Card key={sub.id} className="cgc-card" data-testid={`worker-time-${sub.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{formatDate(sub.date)}</span>
                    </div>
                    <Badge className={cn("text-xs", statusCfg.color)}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {t(`time.${sub.status}`)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">{t('time.dayStart')}</p>
                      <p className="font-mono">{formatTime(sub.day_start)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('time.dayEnd')}</p>
                      <p className="font-mono">{formatTime(sub.day_end)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('time.totalHours')}</p>
                      <p className="font-mono font-bold">{sub.total_hours?.toFixed(1) || 0}{t('time.hours')}</p>
                    </div>
                  </div>
                  {sub.status === 'pending' && (
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                        onClick={() => handleAccept(sub.id)}
                        disabled={actionLoading}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Принять ({sub.total_hours?.toFixed(1)}ч)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                        onClick={() => {
                          setDisputeSub(sub);
                          setDisputeHours(sub.total_hours?.toFixed(1) || '');
                          setDisputeReason('');
                          setDisputeDialogOpen(true);
                        }}
                        disabled={actionLoading}
                      >
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Оспорить
                      </Button>
                    </div>
                  )}
                  {sub.status === 'disputed' && (
                    <div className="mt-3 p-2 rounded bg-purple-50 dark:bg-purple-900/10 text-sm text-purple-700">
                      На рассмотрении у менеджера — вы предложили {sub.disputed_hours?.toFixed(1)}ч
                    </div>
                  )}
                  {sub.status === 'approved' && sub.auto_approved && (
                    <div className="mt-2 text-xs text-muted-foreground">Принято автоматически</div>
                  )}
                  {sub.status === 'approved' && !sub.auto_approved && sub.reviewer_name && (
                    <div className="mt-2 text-xs text-muted-foreground">Подтвердил: {sub.reviewer_name}</div>
                  )}
                  {sub.status === 'rejected' && sub.rejection_reason && (
                    <div className="mt-3 p-2 rounded bg-red-50 dark:bg-red-900/10 text-sm text-red-600">
                      <span className="font-medium">{t('time.rejectionReason')}:</span> {sub.rejection_reason}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Оспорить часы</DialogTitle>
            <DialogDescription>
              Система рассчитала {disputeSub?.total_hours?.toFixed(1)}ч за {disputeSub?.date}.
              Укажите ваше количество часов и причину.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Ваше количество часов *</Label>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={disputeHours}
                onChange={(e) => setDisputeHours(e.target.value)}
                placeholder="8.5"
              />
            </div>
            <div className="space-y-2">
              <Label>Причина * <span className="text-xs text-muted-foreground font-normal">(обязательно)</span></Label>
              <Textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Объясните почему часы должны быть другими..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>Отмена</Button>
            <Button
              onClick={handleDisputeSubmit}
              disabled={actionLoading || !disputeHours || !disputeReason.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Отправить менеджеру
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main component that decides which view to show
export default function TimePage() {
  const { user } = useAuth();
  
  if (user?.role === 'worker') {
    return <WorkerTimePage />;
  }
  
  return <TimeApprovalPage />;
}
